import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import RequireAuth from './auth/RequireAuth';
import MainLayout from './components/MainLayout';
import HomePage from './homePage';
import EventsPage from './eventsPage';

// The home and events pages load straight away; everything else (the map,
// the planner, forms) is downloaded only when it's visited, which keeps the
// first page load on phones small.
const EventPage = lazy(() => import('./eventPage'));
const EventPlan = lazy(() => import('./eventPlan'));
const AccountDetailsPage = lazy(() => import('./accountDetails'));
const CreateUser = lazy(() => import('./createUser'));
const LoginPage = lazy(() => import('./loginPage'));
const ForgotPassword = lazy(() => import('./forgotPassword'));
const ResetPasswordPage = lazy(() => import('./resetPassword'));
const PrivacyPage = lazy(() => import('./privacyPage'));
const FeedbackPage = lazy(() => import('./feedbackPage'));
const SubmitEventPage = lazy(() => import('./submitEventPage'));
const JoinMatePage = lazy(() => import('./joinMatePage'));
const NotFound = lazy(() => import('./notFound'));

const PageLoading: React.FC = () => (
  <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-label="Loading">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-surface-subtle border-t-brand-500" />
  </div>
);

// The events page used to live at /return-results; keep old links (and their filters) working.
const LegacySearchRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/events${search}`} replace />;
};

const AppRouter: React.FC = () => {
  return (
    <Router>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Auth pages (no header/footer) */}
          <Route path="/create-user" element={<CreateUser />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password-email" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Routes with header/footer */}
          <Route element={<MainLayout />}>
            {/* Public: anyone can browse and view events */}
            <Route path="/" element={<HomePage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/event/:eventId" element={<EventPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/submit-event" element={<SubmitEventPage />} />

            {/* Account required */}
            <Route
              path="/event-planner"
              element={
                <RequireAuth>
                  <EventPlan />
                </RequireAuth>
              }
            />
            <Route
              path="/account-details"
              element={
                <RequireAuth>
                  <AccountDetailsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/mates/join/:code"
              element={
                <RequireAuth>
                  <JoinMatePage />
                </RequireAuth>
              }
            />
          </Route>

          <Route path="/return-results" element={<LegacySearchRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRouter;
